import * as React from 'react';
import * as _ from 'underscore';

import { DataQualityType, localizeDataQualityTypeName } from '@tableau/data-catalog-client-components';
import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonType,
  ButtonWidget,
  CheckBoxWithLabelWidget,
  DialogPositioningBehavior,
  DialogType,
  DropdownButtonType,
  List,
  ListProps,
  SingleSelectDropdown,
  TabStyles,
  TextAreaWidget
} from '@tableau/widgets';

import { catalogFeatureFlagsAdapter } from '../../catalog/CatalogFeatureFlagsAdapter';
import { Messages } from '../../codegen/Localize';
import { ReactModalOptions } from '../../ts/services/ReactModalService';
import { characterCounter } from '../content/CharacterCounter';
import {
  ButtonContainerDiv,
  checkboxStyle,
  ContentItemNameDiv,
  DATA_QUALITY_DIALOG_CANCEL_TEST_ID,
  DATA_QUALITY_DIALOG_MESSAGE_ID,
  DATA_QUALITY_DIALOG_SAVE_TEST_ID,
  DATA_QUALITY_DIALOG_SEVERE_TOGGLE_TEST_ID,
  DATA_QUALITY_DIALOG_TEST_ID,
  DATA_QUALITY_DIALOG_TOGGLE_TEST_ID,
  DATA_QUALITY_DIALOG_TYPE_TEST_ID,
  DropDownPromptDiv,
  dropDownStyle,
  HelpTextDiv,
  InputHintDiv,
  textAreaStyle,
  textAreaWidgetClass
} from '../content/styles/DataQualityStyles';
import {
  buttonStyleSubmitting,
  cancelButtonStyle,
  DIALOG_WIDTH,
  dialogBodyStyle
} from '../content/styles/ExternalContentActionsStyles';
import {
  dataQualityTypeListItem,
  dataQualityTypes,
  dataQualityTypeToString,
  stringToDataQualityType
} from './DataQualityType';

const MESSAGE_MAXIMUM_LENGTH: number = 4000;

const defaultInitialState: DataQualityDialogState = {
  isActive: false,
  isSevere: false,
  type: dataQualityTypeToString(DataQualityType.WARNING),
  message: '',
};

export interface DataQualityDialogProps {
  readonly contentName: string;
  readonly initial?: DataQualityDialogState;
  readonly submit: (args: DataQualityDialogState) => void;
  readonly cancel: () => void;
}

export interface DataQualityDialogState {
  readonly isActive: boolean;
  readonly isSevere: boolean;
  readonly type: string;
  readonly message: string;
}

export interface DataQualityDialogStateAndLifecycle extends DataQualityDialogState {
  readonly isSubmitting: boolean;
  readonly isValid: boolean;
}

function getFormState(state: DataQualityDialogState | DataQualityDialogStateAndLifecycle): DataQualityDialogState {
  return {
    isSevere: state.isSevere,
    isActive: state.isActive,
    message: state.message,
    type: state.type
  };
}

function getInitialState(props: DataQualityDialogProps): DataQualityDialogState {
  return getFormState(props.initial || defaultInitialState);
}

function checkIsValid(current: DataQualityDialogState): boolean {
  if (current.message.length > MESSAGE_MAXIMUM_LENGTH) {
    return false;
  }

  return true;
}

function checkIsDirty(current: DataQualityDialogState, initial: DataQualityDialogState): boolean {
  return !_.isEqual(initial, current);
}

function SevereWarningContent(props: {disabled: boolean, isSevere: boolean, setIsSevere: (b: boolean) => void}): JSX.Element | null {
  if (!catalogFeatureFlagsAdapter().catalogSevereWarningsEnabled) {
    return null;
  }

  return (<>
    <CheckBoxWithLabelWidget
      label={Messages.dataQualityDialog_IsSevereCheckboxLabel()}
      testId={DATA_QUALITY_DIALOG_SEVERE_TOGGLE_TEST_ID}
      checked={props.isSevere}
      handleChange={props.setIsSevere}
      removeLeftMargin={true}
      disabled={props.disabled}
      containerStyle={checkboxStyle} />
    <HelpTextDiv>
      {
        Messages.dataQualityDialog_IsSevereHelp()
      }
    </HelpTextDiv>
  </>);
}

export class DataQualityDialog extends React.Component<DataQualityDialogProps, DataQualityDialogStateAndLifecycle> {
  constructor(props: DataQualityDialogProps) {
    super(props);
    const dialogState = props.initial || defaultInitialState;
    this.state = {
      ...dialogState,
      isSubmitting: false,
      isValid: undefined,
    };
  }

  private handleStateChange = <K extends keyof DataQualityDialogState>(key: K, value: DataQualityDialogState[K]) => {
    this.setState((state, props) => {
      const form = getFormState(state);
      const initial: DataQualityDialogState = getInitialState(props);
      const updated: DataQualityDialogState = { ...form, [key]: value };
      const isDirty = checkIsDirty(updated, initial);
      const isValid = isDirty ? checkIsValid(updated) : null;

      return { ...updated, isValid };
    });
  }

  private handleActiveCheckboxChange = (isActive: boolean) => this.handleStateChange('isActive', isActive);

  private handleSevereCheckboxChange = (isSevere: boolean) => this.handleStateChange('isSevere', isSevere);

  private handleTypeChange = (type?: string) => this.handleStateChange('type', type);

  private handleMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => this.handleStateChange('message', event.target.value);

  private onSubmit = () => this.setState({ isSubmitting: true }, () => this.props.submit(getFormState(this.state)));

  private onCancel = () => this.setState({ isSubmitting: true }, this.props.cancel);

  public render(): React.ReactNode {
    return (
      <React.Fragment>
        <ContentItemNameDiv>{this.props.contentName}</ContentItemNameDiv>
        <CheckBoxWithLabelWidget
          label={Messages.dataQualityDialog_IsActiveCheckboxLabel()}
          testId={DATA_QUALITY_DIALOG_TOGGLE_TEST_ID}
          checked={this.state.isActive}
          handleChange={this.handleActiveCheckboxChange}
          removeLeftMargin={true}
          containerStyle={checkboxStyle} />
        <HelpTextDiv>{Messages.dataQualityDialog_IsActiveHelp()}</HelpTextDiv>

        <DropDownPromptDiv>{Messages.dataQualityDialog_DropdownPrompt()}</DropDownPromptDiv>
        <InputHintDiv>{Messages.dataQualityType()}</InputHintDiv>
        <SingleSelectDropdown
          testId={DATA_QUALITY_DIALOG_TYPE_TEST_ID}
          type={DropdownButtonType.Outline}
          list={this.DataQualityTypeList(this.state.type)}
          disabled={!this.state.isActive}
          value={localizeDataQualityTypeName(stringToDataQualityType(this.state.type))}
          onChange={this.handleTypeChange}
          style={dropDownStyle}
          label={''} />
        <SevereWarningContent
          disabled={!this.state.isActive}
          isSevere={this.state.isSevere}
          setIsSevere={this.handleSevereCheckboxChange}
        />

        <TextAreaWidget
          label={<InputHintDiv>{Messages.dataQualityMessage()}</InputHintDiv>}
          onChange={this.handleMessageChange}
          id={DATA_QUALITY_DIALOG_MESSAGE_ID}
          testId={DATA_QUALITY_DIALOG_MESSAGE_ID}
          disabled={!this.state.isActive}
          className={textAreaWidgetClass}
          valid={ this.state.isValid ? undefined : this.state.isValid }
          value={this.state.message}
          style={textAreaStyle}
          message={characterCounter({ value: this.state.message, maximum: MESSAGE_MAXIMUM_LENGTH })} />

        <ButtonContainerDiv>
          <ButtonWidget
            disabled={this.state.isSubmitting}
            testId={DATA_QUALITY_DIALOG_CANCEL_TEST_ID}
            buttonType={ButtonType.Outline}
            handleClick={this.onCancel}
            style={cancelButtonStyle}>
            <span>{Messages.cancel()}</span>
          </ButtonWidget>
          <ButtonWidget
            disabled={this.state.isSubmitting || !this.state.isValid}
            testId={DATA_QUALITY_DIALOG_SAVE_TEST_ID}
            buttonType={ButtonType.Go}
            style={this.state.isSubmitting ? buttonStyleSubmitting : undefined}
            handleClick={this.onSubmit}>
            {
              this.state.isSubmitting ?
                <div style={{ marginTop: TabStyles.Spacing.SmallSpace! }}>
                  <ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Small} testId="submitting" />
                </div> :
                <span>{Messages.save()}</span>
            }
          </ButtonWidget>
        </ButtonContainerDiv>
      </React.Fragment>
    );
  }

  private DataQualityTypeList(selected: string): React.ReactElement<ListProps> {
    return <List items={dataQualityTypes.map(dataQualityTypeListItem)} selectedValue={selected.toString()} />;
  }
}

export function dataQualityDialogProps(props: DataQualityDialogProps): ReactModalOptions {
  return {
    title: Messages.dataQualityDialog_Title(),
    testId: DATA_QUALITY_DIALOG_TEST_ID,
    type: DialogType.NormalWithTitleBar,
    showCloseButton: false,
    positioningBehavior: DialogPositioningBehavior.Centered,
    dialogWidth: DIALOG_WIDTH,
    dialogBodyStyle: dialogBodyStyle,
    dialogContent: <DataQualityDialog {...props} />
  };
}
